import {
    BookmarkBorderedIcon,
    Button,
    chain,
    MenuIcon,
    PlusIcon,
    ShareIcon,
    TrashIcon,
    DraftIcon,
    InProgressIcon
} from '@genestack/ui';
import React from 'react';
import {useLocation} from 'wouter';

import {useCreateQueryDraftMutation} from './hooks/requests/use-create-query-mutation';

import {QueriesTab} from '../../components/layout/page-layout/layout-typings';
import {useSidebarTabs} from '../../hooks/use-sidebar-tabs';
import {usePageLayout} from '../../components/layout/page-layout/use-page-layout';
import {GlobalVersionData} from '../../hooks/global-version/use-global-version-query';
import {VerticalBar, VerticalBarItem} from '../../components/layout/vertical-bar/vertical-bar';
import {wrapWithTooltip} from '../../components/tooltip/wrap-with-tooltip';

const SIDEBAR_TABS: VerticalBarItem[] = [
    {
        tooltip: 'Drafts',
        value: QueriesTab.DRAFTS,
        prepend: <DraftIcon />
    },
    {
        tooltip: 'Running queries',
        value: QueriesTab.RUNNING,
        prepend: <InProgressIcon />
    },
    {
        tooltip: 'My bookmarks',
        value: QueriesTab.FAVORITES,
        prepend: <BookmarkBorderedIcon />
    },
    {
        tooltip: 'All my queries',
        value: QueriesTab.RECENT,
        prepend: <MenuIcon />
    },
    {
        tooltip: 'Shared with me',
        value: QueriesTab.SHARED,
        prepend: <ShareIcon />
    },
    {
        tooltip: 'Recycle bin',
        value: QueriesTab.BIN,
        prepend: <TrashIcon />,
        stickToBottom: true
    }
];

interface Props {
    layoutSettings: ReturnType<typeof usePageLayout>;
    tabSettings: ReturnType<typeof useSidebarTabs>;
    currentGlobalVersion: GlobalVersionData;
}

export function QueriesActivityBar({tabSettings, layoutSettings, currentGlobalVersion}: Props) {
    const createQueryDraft = useCreateQueryDraftMutation();
    const [, navigate] = useLocation();

    const handleCreateNewDocument = async () => {
        const queryInfo = await createQueryDraft.mutateAsync({
            globalVersionId: currentGlobalVersion.id
        });
        layoutSettings.openSidebar();

        navigate(`/${queryInfo.id}`);
    };

    return (
        <VerticalBar
            topControls={wrapWithTooltip(
                <Button
                    data-qa="create-query-button"
                    icon={<PlusIcon />}
                    onClick={handleCreateNewDocument}
                    disabled={createQueryDraft.isPending}
                />,
                'Create new query',
                {placement: 'right'}
            )}
            tabs={SIDEBAR_TABS}
            value={layoutSettings.isSidebarClosed ? undefined : tabSettings.activityBarTab}
            onValueChange={chain(layoutSettings.openSidebar, tabSettings.setActivityBarTab)}
        />
    );
}
